import { Outlet, useRouterState } from "@tanstack/react-router";
import type React from "react";
import { createElement, useEffect } from "react";
import ReactGA from "react-ga4";
import Layout from "../components/layout";
import { canTrackTelemetry, telemetryLocation } from "../telemetry";
import { useTheme } from "../theme/ThemeProvider";
import { RouterPending } from "./components";
import { useActiveRoute } from "./use-active-route";

export function RouterAppShell() {
  const activeRoute = useActiveRoute();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const { layout, setLayout, setPreviousLayout } = useTheme();
  const routeLayout = activeRoute?.layout;

  useEffect(() => {
    if (
      !routeLayout ||
      (routeLayout === layout.type && routeLayout === layout.previousType)
    )
      return;

    setPreviousLayout(routeLayout);
    setLayout(routeLayout);
  }, [
    layout.type,
    layout.previousType,
    routeLayout,
    setLayout,
    setPreviousLayout,
  ]);

  useEffect(() => {
    if (canTrackTelemetry()) {
      ReactGA.send({
        hitType: "pageview",
        page: pathname,
        location: telemetryLocation(),
      });
    }
  }, [pathname]);

  return (
    <div
      data-router-shell="true"
      data-route-owner="tanstack-router-match"
      data-current-route-key={activeRoute?.routeKey}
      data-route-layout={activeRoute?.layout}
      data-route-container={activeRoute?.container}
    >
      {createElement(
        Layout as React.ComponentType<{ content: React.ReactNode }>,
        {
          // Never expose an interactive route inside the previous layout: its
          // replacement can discard user input and mount effects a second time.
          content:
            !routeLayout ||
            (layout.type === routeLayout &&
              layout.previousType === routeLayout) ? (
              <Outlet />
            ) : (
              <RouterPending />
            ),
        },
      )}
    </div>
  );
}
